import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate } from "react-router";
import {
  BRAND_COMMUNICATION_TONES,
  BRAND_CULTURAL_REFERENCE_LEVELS,
  BRAND_EMOJI_USAGES,
  BRAND_SENTENCE_COMPLEXITIES,
  BRAND_SOCIAL_CONFIDENCE_LEVELS,
  BRAND_SOCIAL_CONTENT_STYLES,
  BRAND_SOCIAL_INTENSITY_LEVELS,
  BRAND_SOCIAL_MECHANICS,
  BRAND_VOCABULARY_STYLES,
  BRAND_VOICE_ATTRIBUTES,
} from "@runcheetah/shared/brand-profile";

import {
  CenteredLoader,
  EditableListField,
  EditableTextarea,
  PageIntro,
} from "@/components/common";
import { getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import {
  CONTENT_DNA_MECHANIC_SELECTION_LIMIT,
  toggleContentDnaMechanic,
} from "@/lib/brand-voice-content-dna";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@runcheetah/ui";
import { toast } from "sonner";

type SaveState = "idle" | "saving" | "saved" | "error";

type BrandVoiceForm = {
  tone: string;
  voice: string;
  vocabularyStyle: string;
  sentenceComplexity: string;
  useOfEmoji: string;
  culturalReferences: string;
  contentDnaPrimaryStyle: string;
  contentDnaSecondaryStyle: string;
  contentDnaConfidence: string;
  contentDnaHumorLevel: string;
  contentDnaOpinionatedness: string;
  contentDnaAspirationLevel: string;
  contentDnaEducationLevel: string;
  contentDnaCommunityLevel: string;
  socialMechanics: string[];
  socialAvoidedMechanics: string[];
  socialTruths: string[];
  audienceTensions: string[];
  shareabilityTriggers: string[];
  contentAngles: string[];
  contentAntiPatterns: string[];
  contentDnaRationale: string;
};

type BrandVoiceFormValue = BrandVoiceForm[keyof BrandVoiceForm];

type ContentDnaScalarField =
  | "contentDnaPrimaryStyle"
  | "contentDnaSecondaryStyle"
  | "contentDnaConfidence"
  | "contentDnaHumorLevel"
  | "contentDnaOpinionatedness"
  | "contentDnaAspirationLevel"
  | "contentDnaEducationLevel"
  | "contentDnaCommunityLevel";

type ContentDnaMechanicField = "socialMechanics" | "socialAvoidedMechanics";

type ContentDnaTextListField =
  | "socialTruths"
  | "audienceTensions"
  | "shareabilityTriggers"
  | "contentAngles"
  | "contentAntiPatterns";

const EMPTY_FORM: BrandVoiceForm = {
  tone: "",
  voice: "",
  vocabularyStyle: "",
  sentenceComplexity: "",
  useOfEmoji: "",
  culturalReferences: "",
  contentDnaPrimaryStyle: "",
  contentDnaSecondaryStyle: "",
  contentDnaConfidence: "",
  contentDnaHumorLevel: "",
  contentDnaOpinionatedness: "",
  contentDnaAspirationLevel: "",
  contentDnaEducationLevel: "",
  contentDnaCommunityLevel: "",
  socialMechanics: [],
  socialAvoidedMechanics: [],
  socialTruths: [],
  audienceTensions: [],
  shareabilityTriggers: [],
  contentAngles: [],
  contentAntiPatterns: [],
  contentDnaRationale: "",
};

const FIELD_MAP: Record<keyof BrandVoiceForm, string> = {
  tone: "brandVoiceCommunicationStyle.tone",
  voice: "brandVoiceCommunicationStyle.voice",
  vocabularyStyle: "brandVoiceCommunicationStyle.vocabularyStyle",
  sentenceComplexity: "brandVoiceCommunicationStyle.sentenceComplexity",
  useOfEmoji: "brandVoiceCommunicationStyle.useOfEmoji",
  culturalReferences: "brandVoiceCommunicationStyle.culturalReferences",
  contentDnaPrimaryStyle: "brandVoiceCommunicationStyle.contentDnaPrimaryStyle",
  contentDnaSecondaryStyle: "brandVoiceCommunicationStyle.contentDnaSecondaryStyle",
  contentDnaConfidence: "brandVoiceCommunicationStyle.contentDnaConfidence",
  contentDnaHumorLevel: "brandVoiceCommunicationStyle.contentDnaHumorLevel",
  contentDnaOpinionatedness: "brandVoiceCommunicationStyle.contentDnaOpinionatedness",
  contentDnaAspirationLevel: "brandVoiceCommunicationStyle.contentDnaAspirationLevel",
  contentDnaEducationLevel: "brandVoiceCommunicationStyle.contentDnaEducationLevel",
  contentDnaCommunityLevel: "brandVoiceCommunicationStyle.contentDnaCommunityLevel",
  socialMechanics: "brandVoiceCommunicationStyle.socialMechanics",
  socialAvoidedMechanics: "brandVoiceCommunicationStyle.socialAvoidedMechanics",
  socialTruths: "brandVoiceCommunicationStyle.socialTruths",
  audienceTensions: "brandVoiceCommunicationStyle.audienceTensions",
  shareabilityTriggers: "brandVoiceCommunicationStyle.shareabilityTriggers",
  contentAngles: "brandVoiceCommunicationStyle.contentAngles",
  contentAntiPatterns: "brandVoiceCommunicationStyle.contentAntiPatterns",
  contentDnaRationale: "brandVoiceCommunicationStyle.contentDnaRationale",
};

const formatSocialToken = (value: string): string =>
  value
    .split(/[_-]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

function ContentDnaCard({
  form,
  onScalarChange,
  onListChange,
  onRationaleChange,
}: {
  form: BrandVoiceForm;
  onScalarChange: <K extends ContentDnaScalarField>(
    key: K,
    value: BrandVoiceForm[K],
  ) => Promise<unknown> | unknown;
  onListChange: <K extends ContentDnaMechanicField | ContentDnaTextListField>(
    key: K,
    value: BrandVoiceForm[K],
  ) => Promise<unknown> | unknown;
  onRationaleChange: (value: string) => Promise<unknown> | unknown;
}) {
  const intl = useIntl();
  const scalarFields: Array<{
    key: ContentDnaScalarField;
    label: string;
    description: string;
    options: readonly string[];
  }> = [
    {
      key: "contentDnaPrimaryStyle",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.primaryStyle",
        defaultMessage: "Primary style",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.primaryStyleDescription",
        defaultMessage: "The social content style this brand should optimize around first.",
      }),
      options: BRAND_SOCIAL_CONTENT_STYLES,
    },
    {
      key: "contentDnaSecondaryStyle",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.secondaryStyle",
        defaultMessage: "Secondary style",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.secondaryStyleDescription",
        defaultMessage: "A supporting style for variety without losing strategic fit.",
      }),
      options: BRAND_SOCIAL_CONTENT_STYLES,
    },
    {
      key: "contentDnaConfidence",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.confidence",
        defaultMessage: "Confidence",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.confidenceDescription",
        defaultMessage: "How strong the evidence is for this Content DNA profile.",
      }),
      options: BRAND_SOCIAL_CONFIDENCE_LEVELS,
    },
    {
      key: "contentDnaHumorLevel",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.humorLevel",
        defaultMessage: "Humor level",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.humorLevelDescription",
        defaultMessage: "How much social content should lean into humor.",
      }),
      options: BRAND_SOCIAL_INTENSITY_LEVELS,
    },
    {
      key: "contentDnaOpinionatedness",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.opinionatedness",
        defaultMessage: "Opinionatedness",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.opinionatednessDescription",
        defaultMessage: "How strongly the brand should take positions in social content.",
      }),
      options: BRAND_SOCIAL_INTENSITY_LEVELS,
    },
    {
      key: "contentDnaAspirationLevel",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.aspirationLevel",
        defaultMessage: "Aspiration level",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.aspirationLevelDescription",
        defaultMessage: "How much content should signal identity, lifestyle, and transformation.",
      }),
      options: BRAND_SOCIAL_INTENSITY_LEVELS,
    },
    {
      key: "contentDnaEducationLevel",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.educationLevel",
        defaultMessage: "Education level",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.educationLevelDescription",
        defaultMessage: "How much content should teach, explain, and become saveable.",
      }),
      options: BRAND_SOCIAL_INTENSITY_LEVELS,
    },
    {
      key: "contentDnaCommunityLevel",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.communityLevel",
        defaultMessage: "Community level",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.communityLevelDescription",
        defaultMessage: "How much content should invite participation and shared experience.",
      }),
      options: BRAND_SOCIAL_INTENSITY_LEVELS,
    },
  ];
  const mechanicFields: Array<{
    key: ContentDnaMechanicField;
    label: string;
    description: string;
    unavailableValues: readonly string[];
  }> = [
    {
      key: "socialMechanics",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.socialMechanics",
        defaultMessage: "Preferred mechanics",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.socialMechanicsDescription",
        defaultMessage: "Platform-native structures this brand should lean into for social posts.",
      }),
      unavailableValues: form.socialAvoidedMechanics,
    },
    {
      key: "socialAvoidedMechanics",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.socialAvoidedMechanics",
        defaultMessage: "Avoided mechanics",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.socialAvoidedMechanicsDescription",
        defaultMessage: "Mechanics to keep out when they would dilute or misrepresent the brand.",
      }),
      unavailableValues: form.socialMechanics,
    },
  ];
  const listFields: Array<{
    key: Exclude<ContentDnaTextListField, "audienceTensions">;
    label: string;
    description: string;
    emptyStateLabel: string;
    addLabel: string;
  }> = [
    {
      key: "socialTruths",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.socialTruths",
        defaultMessage: "Social truths",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.socialTruthsDescription",
        defaultMessage:
          "Relatable observations or audience recognition moments this brand can own.",
      }),
      emptyStateLabel: intl.formatMessage({
        id: "brandVoice.contentDna.socialTruthsEmpty",
        defaultMessage: "No social truths yet",
      }),
      addLabel: intl.formatMessage({
        id: "brandVoice.contentDna.socialTruthsAdd",
        defaultMessage: "Add social truth",
      }),
    },
    {
      key: "shareabilityTriggers",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.shareabilityTriggers",
        defaultMessage: "Shareability triggers",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.shareabilityTriggersDescription",
        defaultMessage: "Reasons people would comment, tag a friend, laugh, disagree, or share.",
      }),
      emptyStateLabel: intl.formatMessage({
        id: "brandVoice.contentDna.shareabilityTriggersEmpty",
        defaultMessage: "No shareability triggers yet",
      }),
      addLabel: intl.formatMessage({
        id: "brandVoice.contentDna.shareabilityTriggersAdd",
        defaultMessage: "Add trigger",
      }),
    },
    {
      key: "contentAngles",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.contentAngles",
        defaultMessage: "Content angles",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.contentAnglesDescription",
        defaultMessage:
          "Editable angles generated by combining a Human Lens with an audience Domain Territory.",
      }),
      emptyStateLabel: intl.formatMessage({
        id: "brandVoice.contentDna.contentAnglesEmpty",
        defaultMessage: "No content angles yet",
      }),
      addLabel: intl.formatMessage({
        id: "brandVoice.contentDna.contentAnglesAdd",
        defaultMessage: "Add angle",
      }),
    },
    {
      key: "contentAntiPatterns",
      label: intl.formatMessage({
        id: "brandVoice.contentDna.contentAntiPatterns",
        defaultMessage: "Anti-patterns",
      }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.contentAntiPatternsDescription",
        defaultMessage: "Off-brand content patterns, hooks, or formats this brand should avoid.",
      }),
      emptyStateLabel: intl.formatMessage({
        id: "brandVoice.contentDna.contentAntiPatternsEmpty",
        defaultMessage: "No anti-patterns yet",
      }),
      addLabel: intl.formatMessage({
        id: "brandVoice.contentDna.contentAntiPatternsAdd",
        defaultMessage: "Add anti-pattern",
      }),
    },
  ];

  return (
    <Card className="border-border/70 bg-card/90 shadow-sm">
      <CardHeader className="space-y-2 pb-4">
        <CardTitle className="text-lg">
          <FormattedMessage id="brandVoice.contentDna.title" defaultMessage="Content DNA" />
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          <FormattedMessage
            id="brandVoice.contentDna.description"
            defaultMessage="The extracted social style, tensions, mechanics, and repeatable angles Cheetah should use when planning social posts."
          />
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
          {scalarFields.map((field) => (
            <EditableEnum
              key={field.key}
              label={field.label}
              description={field.description}
              value={form[field.key]}
              options={field.options}
              onChange={(value) => onScalarChange(field.key, value)}
            />
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {mechanicFields.map((field) => (
            <EditableMechanicList
              key={field.key}
              label={field.label}
              description={field.description}
              values={form[field.key]}
              unavailableValues={field.unavailableValues}
              onChange={(value) => onListChange(field.key, value)}
            />
          ))}

          <div className="space-y-3 rounded-2xl border border-border/70 bg-background/55 p-4">
            <div>
              <p className="text-sm font-medium">
                {intl.formatMessage({
                  id: "brandVoice.contentDna.audienceTensions",
                  defaultMessage: "Hook tensions",
                })}
              </p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {intl.formatMessage({
                  id: "brandVoice.contentDna.audienceTensionsDescription",
                  defaultMessage:
                    "Read-only tensions derived from the deep audience research and used to power hooks.",
                })}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.audienceTensions.length ? (
                form.audienceTensions.map((tension) => (
                  <span
                    key={tension}
                    className="rounded-full border border-border bg-muted px-3 py-1.5 text-sm"
                  >
                    {tension}
                  </span>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  {intl.formatMessage({
                    id: "brandVoice.contentDna.audienceTensionsEmpty",
                    defaultMessage: "No hook tensions available from audience research yet",
                  })}
                </p>
              )}
            </div>
          </div>

          {listFields.map((field) => (
            <div
              key={field.key}
              className="space-y-3 rounded-2xl border border-border/70 bg-background/55 p-4"
            >
              <EditableListField
                label={field.label}
                description={field.description}
                items={form[field.key]}
                emptyStateLabel={field.emptyStateLabel}
                addLabel={field.addLabel}
                onSave={(items) => onListChange(field.key, items)}
              />
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-border/70 bg-background/55 p-4">
          <EditableTextarea
            label={intl.formatMessage({
              id: "brandVoice.contentDna.rationale",
              defaultMessage: "Rationale",
            })}
            description={intl.formatMessage({
              id: "brandVoice.contentDna.rationaleDescription",
              defaultMessage: "Why this Content DNA fits the brand facts and audience.",
            })}
            value={form.contentDnaRationale}
            onChange={() => undefined}
            onSave={onRationaleChange}
            placeholder={intl.formatMessage({
              id: "brandVoice.contentDna.rationalePlaceholder",
              defaultMessage: "Explain why this Content DNA fits the brand...",
            })}
            rows={5}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function EditableMechanicList({
  label,
  description,
  values,
  unavailableValues,
  onChange,
}: {
  label: string;
  description: string;
  values: readonly string[];
  unavailableValues: readonly string[];
  onChange: (values: string[]) => Promise<unknown> | unknown;
}) {
  const [saveState, setSaveState] = useState<SaveState>("idle");

  const handleToggle = async (mechanic: string) => {
    const nextValues = toggleContentDnaMechanic({
      selected: values,
      mechanic,
      unavailable: unavailableValues,
    });

    if (
      nextValues.length === values.length &&
      nextValues.every((value, index) => value === values[index])
    ) {
      return;
    }

    setSaveState("saving");

    try {
      await onChange(nextValues);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  };

  return (
    <div className="space-y-3 rounded-2xl border border-border/70 bg-background/55 p-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs leading-5 text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {BRAND_SOCIAL_MECHANICS.map((mechanic) => {
          const selected = values.includes(mechanic);
          const disabled = !selected && unavailableValues.includes(mechanic);
          const limitReached = !selected && values.length >= CONTENT_DNA_MECHANIC_SELECTION_LIMIT;

          return (
            <button
              key={mechanic}
              type="button"
              onClick={() => handleToggle(mechanic)}
              aria-pressed={selected}
              disabled={disabled || limitReached || saveState === "saving"}
              className={[
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                selected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border/70 bg-background/70 text-foreground hover:border-primary/60 hover:text-primary",
                disabled || limitReached
                  ? "cursor-not-allowed opacity-45 hover:border-border/70 hover:text-foreground"
                  : "",
              ].join(" ")}
            >
              {formatSocialToken(mechanic)}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        <FormattedMessage
          id="brandVoice.contentDna.mechanicLimit"
          defaultMessage="Select up to {limit} mechanics. Preferred and avoided mechanics cannot overlap."
          values={{ limit: CONTENT_DNA_MECHANIC_SELECTION_LIMIT }}
        />
      </p>
      {saveState !== "idle" ? (
        <p className="text-xs text-muted-foreground">
          {saveState === "saving" ? (
            <FormattedMessage id="common.saving" defaultMessage="Saving..." />
          ) : null}
          {saveState === "saved" ? (
            <FormattedMessage id="common.saved" defaultMessage="Saved" />
          ) : null}
          {saveState === "error" ? (
            <FormattedMessage
              id="common.saveErrorTryAgain"
              defaultMessage="Could not save. Try again."
            />
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

function EditableEnum({
  label,
  description,
  value,
  options,
  onChange,
}: {
  label: string;
  description: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => Promise<unknown> | unknown;
}) {
  const intl = useIntl();
  const [saveState, setSaveState] = useState<SaveState>("idle");

  const handleChange = async (nextValue: string) => {
    setSaveState("saving");

    try {
      await onChange(nextValue);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  };

  return (
    <div className="space-y-3 rounded-2xl border border-border/70 bg-background/55 p-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs leading-5 text-muted-foreground">{description}</p>
      </div>
      <Select value={value} onValueChange={handleChange}>
        <SelectTrigger className="w-full">
          <SelectValue
            placeholder={intl.formatMessage({
              id: "brandVoice.selectPlaceholder",
              defaultMessage: "Select...",
            })}
          />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              <span>{formatSocialToken(option)}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {saveState !== "idle" ? (
        <p className="text-xs text-muted-foreground">
          {saveState === "saving" ? (
            <FormattedMessage id="common.saving" defaultMessage="Saving..." />
          ) : null}
          {saveState === "saved" ? (
            <FormattedMessage id="common.saved" defaultMessage="Saved" />
          ) : null}
          {saveState === "error" ? (
            <FormattedMessage
              id="common.saveErrorTryAgain"
              defaultMessage="Could not save. Try again."
            />
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

export default function BrandVoicePage() {
  const intl = useIntl();
  const { brand, brandQuery } = useRouteBrand();
  const utils = trpc.useUtils();

  const query = trpc.brand.getBrandVoice.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: Infinity },
  );
  const mutation = trpc.brand.updateBrandVoice.useMutation({
    onSuccess: async () => {
      if (!brand?.id) return;

      await Promise.all([
        utils.brand.getBrandVoice.invalidate({ brandId: brand.id }),
        utils.brand.getBrandProfile.invalidate({ brandId: brand.id }),
        utils.brand.getStrategyRegenerationStatus.invalidate({ brandId: brand.id }),
      ]);
    },
  });

  const [form, setForm] = useState<BrandVoiceForm>(EMPTY_FORM);

  useEffect(() => {
    if (query.data) {
      setForm({
        tone: query.data.tone ?? "",
        voice: query.data.voice ?? "",
        vocabularyStyle: query.data.vocabularyStyle ?? "",
        sentenceComplexity: query.data.sentenceComplexity ?? "",
        useOfEmoji: query.data.useOfEmoji ?? "",
        culturalReferences: query.data.culturalReferences ?? "",
        contentDnaPrimaryStyle: query.data.contentDnaPrimaryStyle ?? "",
        contentDnaSecondaryStyle: query.data.contentDnaSecondaryStyle ?? "",
        contentDnaConfidence: query.data.contentDnaConfidence ?? "",
        contentDnaHumorLevel: query.data.contentDnaHumorLevel ?? "",
        contentDnaOpinionatedness: query.data.contentDnaOpinionatedness ?? "",
        contentDnaAspirationLevel: query.data.contentDnaAspirationLevel ?? "",
        contentDnaEducationLevel: query.data.contentDnaEducationLevel ?? "",
        contentDnaCommunityLevel: query.data.contentDnaCommunityLevel ?? "",
        socialMechanics: query.data.socialMechanics ?? [],
        socialAvoidedMechanics: query.data.socialAvoidedMechanics ?? [],
        socialTruths: query.data.socialTruths ?? [],
        audienceTensions: query.data.audienceTensions ?? [],
        shareabilityTriggers: query.data.shareabilityTriggers ?? [],
        contentAngles: query.data.contentAngles ?? [],
        contentAntiPatterns: query.data.contentAntiPatterns ?? [],
        contentDnaRationale: query.data.contentDnaRationale ?? "",
      });
    }
  }, [query.data]);

  const set = <K extends keyof BrandVoiceForm>(key: K, value: BrandVoiceForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const saveField = (fieldPath: string, value: BrandVoiceFormValue) => {
    if (!brand?.id) return;

    return mutation.mutateAsync({ brandId: brand.id, fields: { [fieldPath]: value } }).catch(() => {
      toast.error(
        intl.formatMessage({
          id: "common.couldNotSaveChanges",
          defaultMessage: "Could not save changes",
        }),
      );
      throw new Error("save_failed");
    });
  };

  if (brandQuery.isPending || query.isPending) {
    return <CenteredLoader />;
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <PageIntro
        title={intl.formatMessage({
          id: "brandVoice.title",
          defaultMessage: "Voice & Content DNA",
        })}
        titleClassName="text-balance"
      />

      <Card className="border-border/70 bg-card/90 shadow-sm">
        <CardHeader className="space-y-2 pb-4">
          <CardTitle className="text-lg">
            <FormattedMessage id="brandVoice.settingsTitle" defaultMessage="Voice Basics" />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <EditableEnum
            label={intl.formatMessage({ id: "brandVoice.tone.label", defaultMessage: "Tone" })}
            description={intl.formatMessage({
              id: "brandVoice.tone.description",
              defaultMessage: "Overall emotional temperature of the messaging.",
            })}
            value={form.tone}
            options={BRAND_COMMUNICATION_TONES}
            onChange={async (value) => {
              set("tone", value);
              await saveField(FIELD_MAP.tone, value);
            }}
          />
          <EditableEnum
            label={intl.formatMessage({ id: "brandVoice.voice.label", defaultMessage: "Voice" })}
            description={intl.formatMessage({
              id: "brandVoice.voice.description",
              defaultMessage: "The personality trait that should come through consistently.",
            })}
            value={form.voice}
            options={BRAND_VOICE_ATTRIBUTES}
            onChange={async (value) => {
              set("voice", value);
              await saveField(FIELD_MAP.voice, value);
            }}
          />
          <EditableEnum
            label={intl.formatMessage({
              id: "brandVoice.vocabulary.label",
              defaultMessage: "Vocabulary",
            })}
            description={intl.formatMessage({
              id: "brandVoice.vocabulary.description",
              defaultMessage: "How plainspoken, expert, or elevated the wording should feel.",
            })}
            value={form.vocabularyStyle}
            options={BRAND_VOCABULARY_STYLES}
            onChange={async (value) => {
              set("vocabularyStyle", value);
              await saveField(FIELD_MAP.vocabularyStyle, value);
            }}
          />
          <EditableEnum
            label={intl.formatMessage({
              id: "brandVoice.sentenceComplexity.label",
              defaultMessage: "Sentence Complexity",
            })}
            description={intl.formatMessage({
              id: "brandVoice.sentenceComplexity.description",
              defaultMessage: "Whether the writing should feel fast and direct or more layered.",
            })}
            value={form.sentenceComplexity}
            options={BRAND_SENTENCE_COMPLEXITIES}
            onChange={async (value) => {
              set("sentenceComplexity", value);
              await saveField(FIELD_MAP.sentenceComplexity, value);
            }}
          />
          <EditableEnum
            label={intl.formatMessage({
              id: "brandVoice.emojiUsage.label",
              defaultMessage: "Emoji Usage",
            })}
            description={intl.formatMessage({
              id: "brandVoice.emojiUsage.description",
              defaultMessage: "Whether emojis are part of the brand voice or should be rare.",
            })}
            value={form.useOfEmoji}
            options={BRAND_EMOJI_USAGES}
            onChange={async (value) => {
              set("useOfEmoji", value);
              await saveField(FIELD_MAP.useOfEmoji, value);
            }}
          />
          <div className="lg:col-span-2 xl:col-span-3">
            <EditableEnum
              label={intl.formatMessage({
                id: "brandVoice.culturalReferences.label",
                defaultMessage: "Cultural References",
              })}
              description={intl.formatMessage({
                id: "brandVoice.culturalReferences.description",
                defaultMessage: "How current, niche, or culture-aware the brand should sound.",
              })}
              value={form.culturalReferences}
              options={BRAND_CULTURAL_REFERENCE_LEVELS}
              onChange={async (value) => {
                set("culturalReferences", value);
                await saveField(FIELD_MAP.culturalReferences, value);
              }}
            />
          </div>
        </CardContent>
      </Card>

      <ContentDnaCard
        form={form}
        onScalarChange={(key, value) => {
          set(key, value);
          return saveField(FIELD_MAP[key], value);
        }}
        onListChange={(key, value) => {
          set(key, value);
          return saveField(FIELD_MAP[key], value);
        }}
        onRationaleChange={(value) => {
          set("contentDnaRationale", value);
          return saveField(FIELD_MAP.contentDnaRationale, value);
        }}
      />
    </div>
  );
}
